import { useId } from 'react';
import Image from 'next/image';

import { Box, Heading, Paragraph, Text, ToggleCard, Tooltip, TriggerText } from '@zillow/constellation-10';

import { PROPERTY_COUNT_OPTIONS } from '../../../../../constants/AccountSettingsConstants';
import { PROPERTY_COUNT_RANGE_ILLUSTRATION_URL } from '../../../../../constants/DigitalLibraryConstants';

import type { PropertyCountRangeType } from '@zg-rentals/ts-rental-manager-api-client';

type PropertyCountRangeSelectProps = {
  value?: PropertyCountRangeType;
  onChange: (value: PropertyCountRangeType) => void;
  questionNumber: number;
  questionTotal: number;
  disabled?: boolean;
};

export const PropertyCountRangeSelect = ({
  value,
  onChange,
  questionNumber,
  questionTotal,
  disabled: _disabled = false,
}: PropertyCountRangeSelectProps) => {
  const questionHeadingId = useId();

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '500',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <Box aria-hidden="true">
        <Image priority src={PROPERTY_COUNT_RANGE_ILLUSTRATION_URL} alt="" width={120} height={120} />
      </Box>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
        <Text textStyle="body-xs-bold" css={{ color: 'text.subtle' }}>
          Question {questionNumber} of {questionTotal}
        </Text>
        <Heading id={questionHeadingId} level={1} textStyle="heading-xs">
          How many units do you manage?
        </Heading>
        <Paragraph>Units can be single-family homes or individual units in a building.</Paragraph>
        <Tooltip.Root placement="bottom">
          <Tooltip.Trigger>
            <TriggerText css={{ display: 'inline-block', width: 'fit-content' }}>Get an example</TriggerText>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content>
              <Paragraph>Example: One single-family home and one duplex would be 3 units total.</Paragraph>
              <Tooltip.Arrow />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Box>
      <Box
        role="group"
        aria-labelledby={questionHeadingId}
        css={{ display: 'flex', flexDirection: 'column', gap: '400', width: '100%' }}
      >
        {PROPERTY_COUNT_OPTIONS.map(({ value: optionValue, label }) => {
          const isSelected = value === optionValue;

          return (
            <ToggleCard
              key={optionValue}
              elevated={false}
              selected={isSelected}
              aria-label={label}
              onSelectedChange={() => onChange(optionValue)}
            >
              <Text>{label}</Text>
            </ToggleCard>
          );
        })}
      </Box>
    </Box>
  );
};

export default PropertyCountRangeSelect;
